import { useAppSelector, useAppStore } from 'hooks/redux';
import { List } from 'immutable';
import _get from 'lodash/get';
import React, { useMemo, useState } from 'react';

import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import {
  chartOfAccountsSelectors,
  type chartOfAccountsTypes,
  chartOfAccountsUtils,
} from 'app-components/flux/chart-of-accounts';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { customFrequencyUtils, scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import type {
  ScheduledTransaction,
  ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors, transactionsTypes } from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { getStandardDrawerFloatingSx } from 'components/Drawers/StandardDrawer';
import AccountField from 'components/QuickenControls/AccountField';
import ConnectedAvatar from 'components/ConnectedAvatar';
import MuiTagsField from 'components/QuickenControls/TagsField/TagsField';
import QAmountField from 'components/QAmountField';
import QPanelButton from 'components/QPanelButton';
import TransactionSplits from 'components/TransactionSplits';
import {
  getExcludedFromReportsDisplayState,
  getExcludedFromSpendingPlanDisplayState,
} from 'components/Transactions/VisibilitySection/utils';
import UsageWithCategory from 'components/UsageWithCategory';
import { splitItemsToArray } from 'app-components/flux/transactions/splitsHelpers';
import QFrequency from 'components/QFrequency';
import AmountCard from 'components/Series/AmountCard';
import ExclusionsButton, { type ExclusionKey } from 'components/Series/ExclusionsButton';
import SplitExclusionNotice from 'components/Series/SplitExclusionNotice';
import { useCommittedTextDraft } from 'components/Series/useCommittedTextDraft';

import { applySeriesAccountChange } from './accountSelection';
import { applySeriesCategoryF2SExclusion, getSeriesCategoryFieldError } from './categorySelection';
import { deriveScheduledTransactionSeriesType, type SeriesType } from './typeSelection';

import BillConnectButton from '../components/BillConnectButton';
import { useSeriesDetailCardStyles } from '../components/styles';
import { useScheduledTransactionEditFormAccessors } from '../hooks/useScheduledTransactionEditFormFields';
import type { ScheduledTransactionEditFormAccessors } from '../types';
import SeriesPayeeField from '../components/SeriesPayeeField';

const SERIES_TYPE_LABEL: Record<SeriesType, string> = {
  BILL: 'Bill',
  SUBSCRIPTION: 'Subscription',
  TRANSFER: 'Transfer',
  INCOME: 'Income',
};

interface BasicTabProps {
  form: ScheduledTransactionEditFormAccessors;
  scheduledTransaction: ScheduledTransaction | ScheduledTransactionType;
  create?: boolean;
  subscriptionInitial?: boolean;
  setTabValue: (a: number) => void;
  showBillConnect?: boolean;
  onAssignSplits?: () => void;
  onEndSeries?: () => void;
}

function BasicTab(props: BasicTabProps) {
  const { classes, cx } = useSeriesDetailCardStyles();
  const theme = useTheme();
  const store = useAppStore();
  const drawerFloatingSx = getStandardDrawerFloatingSx();

  const {
    form: formAccessors,
    scheduledTransaction,
    create,
    setTabValue,
    showBillConnect,
    onAssignSplits,
    onEndSeries,
  } = props;
  const form = useScheduledTransactionEditFormAccessors(formAccessors);

  const { values } = form;
  const { errors, touched } = form.validation;
  const { transaction } = form.values || {};
  const valTxn = (transaction || {}) as TransactionProps;
  const splitRecord = valTxn.split ? transactionsTypes.mkSplit(valTxn.split as any) : null;
  const splitCount = splitRecord?.items ? splitItemsToArray(splitRecord.items).length : 0;
  const hasSplitsAssigned = splitCount > 0;

  // Local draft, not bound straight to `form.setters` — an emptied name
  // should just revert to the last committed payee on blur, matching the
  // Series details pane's Name field (BillScoutSetup's SeriesDetailContent).
  const {
    draft: payeeDraft,
    setDraft: setPayeeDraft,
    commit: commitPayee,
  } = useCommittedTextDraft({
    committedValue: transaction.payee,
    onCommit: (trimmed) => form.setters.setTransactionPayee(trimmed),
  });

  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  const amountMatchVarianceFF = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('matchAmountVariants'),
  );
  const transactionsById = useAppSelector(transactionsSelectors.getResourcesById);
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const categoriesById = useAppSelector(categoriesSelectors.getCategoriesById);
  const incomeCOAIds = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const businessCOAIds = useAppSelector(categoriesSelectors.getBusinessCoaIdsSet);
  const [excludeIncomeByDefault] = usePreferenceV2('sSPExcludeIncomeByDefault');
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);

  const [focus, setFocus] = useState<string | null>(null);
  const [excludeF2SOverridden, setExcludeF2SOverridden] = useState(false);
  const getField = <T,>(name: string) => ({
    name,
    value: _get(values, name) as T,
    onChange: (event: { target: { value: T } }) => form.setters.setFieldValue(name, event.target.value),
    onBlur: () => undefined,
  });

  const state = store.getState();
  const seriesPrimaryCoa = scheduledTransactionsUtils.getSeriesPrimaryCoa(valTxn);
  const hasPrimarySplitCoa = Boolean(hasSplitsAssigned && seriesPrimaryCoa);
  const splitPrimaryIsTransfer = Boolean(seriesPrimaryCoa && chartOfAccountsUtils.isTransferCOA(seriesPrimaryCoa));
  const splitPrimaryIsIncome = Boolean(
    seriesPrimaryCoa?.type === 'CATEGORY' && categoriesSelectors.isIncomeCat(state, seriesPrimaryCoa.id),
  );
  const splitPrimaryIsExpense = Boolean(hasPrimarySplitCoa && !splitPrimaryIsTransfer && !splitPrimaryIsIncome);
  const disableTypeChange = Boolean(hasSplitsAssigned && (splitPrimaryIsTransfer || splitPrimaryIsIncome));

  const isIncome = scheduledTransactionsUtils.isIncomeSeriesFromTxn(valTxn, incomeCOAIds);
  const selectedType: SeriesType = deriveScheduledTransactionSeriesType(state, valTxn);
  const selectableSeriesTypes: SeriesType[] = splitPrimaryIsExpense
    ? ['BILL', 'SUBSCRIPTION']
    : ['BILL', 'SUBSCRIPTION', 'TRANSFER', 'INCOME'];

  // If category opening balance or balance adjustment is selected, they should be ignored and unable to be changed back.
  const disableReports = chartOfAccountsUtils.disableExcludeReports(valTxn?.coa);
  const excludeF2SDisplayFromTxn = useMemo(
    () =>
      getExcludedFromSpendingPlanDisplayState(
        {
          isExcludedFromF2S: transaction.isExcludedFromF2S,
          split: transaction.split,
        },
        transactionsById,
      ),
    [transaction.isExcludedFromF2S, transaction.split, transactionsById],
  );
  const excludeF2SCheckboxState = useMemo(() => {
    if (excludeF2SOverridden) {
      return { value: Boolean(transaction.isExcludedFromF2S), indeterminate: false };
    }
    return excludeF2SDisplayFromTxn;
  }, [excludeF2SDisplayFromTxn, excludeF2SOverridden, transaction.isExcludedFromF2S]);
  const excludeReportsDisplayFromTxn = useMemo(
    () =>
      getExcludedFromReportsDisplayState(
        {
          isExcludedFromReports: transaction.isExcludedFromReports,
          split: transaction.split,
        },
        transactionsById,
      ),
    [transaction.isExcludedFromReports, transaction.split, transactionsById],
  );
  const visibilityF2SDisplay = useMemo(
    () => (hasSplitsAssigned ? excludeF2SDisplayFromTxn : excludeF2SCheckboxState),
    [excludeF2SCheckboxState, excludeF2SDisplayFromTxn, hasSplitsAssigned],
  );
  const visibilityExcludeReportsDisplay = useMemo(
    () =>
      hasSplitsAssigned
        ? excludeReportsDisplayFromTxn
        : { value: transaction.isExcludedFromReports, indeterminate: false as const },
    [excludeReportsDisplayFromTxn, hasSplitsAssigned, transaction.isExcludedFromReports],
  );

  const updateUsageAndCategory = async ({
    usage: newUsage,
    coa: newCoa,
  }: {
    usage: string;
    coa: chartOfAccountsTypes.ChartOfAccountProps;
  }) => {
    const acc = accountsById.get(transaction?.accountId);
    const previousCoa = transaction?.coa;

    applySeriesCategoryF2SExclusion({
      newCoa,
      previousCoa,
      account: acc,
      state,
      create: Boolean(create),
      excludeIncomeByDefault,
      setFieldValue: form.setters.setFieldValue,
    });

    if (chartOfAccountsUtils.isTransferCOA(newCoa)) {
      form.setters.setFieldValue('transaction.isExcludedFromReports', true);
      form.setters.setFieldValue('transaction.excludeReportsDest', true);
    } else if (!acc?.isExcludedFromReports && chartOfAccountsUtils.isTransferCOA(previousCoa)) {
      form.setters.setFieldValue('transaction.isExcludedFromReports', false);
      form.setters.setFieldValue('transaction.excludeReportsDest', false);
    }

    // bill + sub flag updates
    if (chartOfAccountsSelectors.isIncomeOrTransfer(state, newCoa)) {
      // force incomes and transfer to nothing
      form.setters.setFieldValue('transaction.isBill', false);
      form.setters.setFieldValue('transaction.isSubscription', false);

      if (chartOfAccountsUtils.isTransferCOA(newCoa)) {
        form.setters.setFieldValue('transaction.isTransfer', true);
        form.setters.setFieldValue('transaction.isIncome', false);
      } else {
        form.setters.setFieldValue('transaction.isTransfer', false);
        form.setters.setFieldValue('transaction.isIncome', true);
      }
    } else {
      // Preserve the user's chosen Bill/Subscription type when changing category.
      const wantsSubscription = Boolean(form.values?.transaction?.isSubscription);
      form.setters.setFieldValue('transaction.isTransfer', false);
      form.setters.setFieldValue('transaction.isIncome', false);
      form.setters.setFieldValue('transaction.isBill', !wantsSubscription);
      form.setters.setFieldValue('transaction.isSubscription', wantsSubscription);
    }
    if (create && transaction?.amount < 0 && categoriesSelectors.isIncomeCat(null, newCoa.id)) {
      // on create, changed to income cat on create, flip sign
      form.setters.setFieldValue('transaction.amount', Number(transaction.amount * -1));
    } else if (
      create &&
      categoriesSelectors.isIncomeCat(null, transaction.coa?.id || '') &&
      !categoriesSelectors.isIncomeCat(null, newCoa.id) &&
      transaction.amount > 0
    ) {
      // on create, changed from income to expense on create, flip sign back
      form.setters.setFieldValue('transaction.amount', Number(transaction.amount * -1));
    }
    await form.setters.setFieldValue('transaction.coa', newCoa);
    form.setters.setFieldValue('transaction.businessId', newUsage);
  };

  const setType = (nextType: SeriesType) => {
    form.setters.setSeriesType(nextType);
  };

  const onToggleExclusion = (key: ExclusionKey) => {
    if (key === 'spendingPlan') {
      setExcludeF2SOverridden(true);
      form.setters.setFieldValue('transaction.excludeF2SUserOverridden', true);
      form.setters.setFieldValue('transaction.isExcludedFromF2S', !(visibilityF2SDisplay.value ?? false));
    } else {
      form.setters.setFieldValue('transaction.isExcludedFromReports', !transaction.isExcludedFromReports);
    }
  };

  // # ===================================================================================== #
  // # -----                                Renderers                                  ----- #
  // # ===================================================================================== #

  // @ts-expect-error FIXME TS7006
  const renderDateAndFrequencySummary = (form) => {
    const { virtualRecurrence, dueOn, virtualEndOn, virtualFrequency } = form.values || {};
    const { text: summaryText } = customFrequencyUtils.recurrenceInfo({
      recurrence: virtualRecurrence,
      startDate: dueOn,
      endDate: virtualEndOn,
    });

    return (
      <QFrequency
        id='setDateAndFreqInitial'
        dueOn={dueOn}
        endOn={virtualEndOn}
        frequency={virtualFrequency}
        summaryText={summaryText}
        onClick={() => {
          tracker.track(tracker.events.viewFrequencyDetails);
          setTabValue(1);
        }}
      />
    );
  };

  const tags = form.values?.transaction?.tags;
  const tagsValue = Array.isArray(tags) ? tags : tags?.toArray() || [];

  // Main render
  return (
    <Box className={classes.tabContent}>
      <Box className={classes.section}>
        <Box className={classes.nameRow}>
          <ConnectedAvatar
            seriesIcon
            isCurrentCategoryIncome={isIncome}
            model={scheduledTransaction}
            txn={valTxn}
            size={56}
            iconSize={24}
            editable
            // Fold the icon into the form instead of letting ConnectedAvatar dispatch a
            // discrete update: the series may not exist yet (Create), and even in Edit mode
            // a standalone dispatch would fall out of sync with the tab's Update/discard flow
            // (Update would clobber it with the form's stale logoId, or a dismiss would leave
            // it persisted while every other field on the tab was discarded).
            onIconChange={({ logoId, logoUrl }) => {
              void form.setters.setFieldValue('transaction.logoId', logoId);
              void form.setters.setFieldValue('transaction.logoUrl', logoUrl);
            }}
          />
          <SeriesPayeeField
            value={payeeDraft}
            onChange={(event) => setPayeeDraft(event.target.value)}
            textFieldProps={{
              label: '',
              placeholder: 'Series name',
              'aria-label': 'Series name',
              variant: 'outlined',
              size: 'small',
              slotProps: {
                input: { style: { ...theme.typography.titleMedium } },
                htmlInput: { style: { padding: 0 } },
              },
              className: classes.payeeInput,
              onBlur: commitPayee,
              onKeyDown: (event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitPayee();
                }
              },
            }}
            error={Boolean(_get(errors, 'transaction.payee'))}
            helperText={_get(errors, 'transaction.payee')}
          />
        </Box>

        <Box className={classes.fieldRow}>
          <Box className={classes.halfWidthField}>
            <AccountField
              {...getField<string>('transaction.accountId')}
              key={transaction.accountId}
              // @ts-expect-error FIXME TS7006
              onSelected={(account) => {
                if (account) {
                  const previousAcc = accountsById.get(transaction?.accountId);
                  applySeriesAccountChange({
                    account,
                    previousAccount: previousAcc,
                    currentCoa: transaction.coa,
                    currentIsSubscription: Boolean(valTxn.isSubscription),
                    isBillForAccount: Boolean(chartOfAccountsSelectors.isBill(state, account.id, valTxn && valTxn.coa)),
                    touchedBill: Boolean(touched.transaction?.isBill),
                    touchedSubscription: Boolean(touched.transaction?.isSubscription),
                    businessEnabled,
                    excludeIncomeByDefault,
                    incomeCoaIds: incomeCOAIds,
                    businessCoaIds: businessCOAIds,
                    setFieldValue: form.setters.setFieldValue,
                    categoriesById,
                    accountsById,
                    currentSplitItems: splitRecord?.items ? splitItemsToArray(splitRecord.items) : null,
                  });
                  setFocus('transaction.accountId');
                  form.setters.setFieldValue('transaction.accountId', account.id);
                }
              }}
              initialAccountId={transaction.accountId}
              autoFocus={focus === 'transaction.accountId'}
              id='account'
              label='Select Account'
              vairant='filled'
              autoComplete='one-time-code'
              fullWidth
              doNotIncludeClosed
              error={Boolean(_get(errors, 'transaction.accountId'))}
              helperText={_get(errors, 'transaction.accountId')}
            />
          </Box>
          <Box className={cx(classes.halfWidthField, classes.typeField)}>
            <Tooltip
              title={
                disableTypeChange
                  ? "Type can't be changed for split series. It's derived from the first split line."
                  : ''
              }
              arrow
              placement='top'
              disableHoverListener={!disableTypeChange}
            >
              <span>
                <FormControl variant='filled' fullWidth disabled={disableTypeChange}>
                  <InputLabel id='series-type-label'>Type</InputLabel>
                  <Select
                    MenuProps={{ sx: drawerFloatingSx }}
                    id='series-type-select'
                    labelId='series-type-label'
                    value={selectedType}
                    onChange={(event) => setType(event.target.value as SeriesType)}
                    renderValue={(value) => (
                      <Typography variant='bodyRegular'>{SERIES_TYPE_LABEL[value as SeriesType]}</Typography>
                    )}
                    inputProps={{ 'aria-label': 'Type' }}
                    disabled={disableTypeChange}
                    className={classes.typeSelect}
                  >
                    {selectableSeriesTypes.map((seriesTypeOption) => (
                      <MenuItem key={seriesTypeOption} value={seriesTypeOption}>
                        {SERIES_TYPE_LABEL[seriesTypeOption]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {splitSeriesFf && hasSplitsAssigned ? null : (
          <Box className={classes.fieldRow}>
            <Box className={classes.halfWidthField}>
              <UsageWithCategory
                {...getField<TransactionProps>('transaction')}
                accountId={transaction.accountId}
                categoryProps={{
                  textFieldVariant: 'filled',
                  label: 'Category',
                  editable: true,
                  fullWidth: true,
                  value: transaction.coa,
                  error: getSeriesCategoryFieldError({
                    error: _get(errors, 'transaction'),
                    touched: _get(touched, 'transaction'),
                  }).error,
                  helperText: getSeriesCategoryFieldError({
                    error: _get(errors, 'transaction'),
                    touched: _get(touched, 'transaction'),
                  }).helperText,
                }}
                onChange={updateUsageAndCategory}
                usageProps={{
                  value: transaction.businessId,
                }}
              />
            </Box>
            <Box className={classes.halfWidthField}>
              <MuiTagsField
                {...getField<TransactionProps['tags']>('transaction.tags')}
                value={tagsValue}
                id='transaction-tags'
                onChange={(_e, tagArray, _reason, _details) =>
                  form.setters.setFieldValue('transaction.tags', List(tagArray))
                }
              />
            </Box>
          </Box>
        )}

        <Box className={classes.splitsColumn}>
          <Box className={classes.fieldRow}>
            {splitSeriesFf ? (
              <Box className={classes.halfWidthField}>
                <TransactionSplits
                  transaction={valTxn}
                  onAssignSplits={onAssignSplits}
                  variant='series'
                  includeOverflowSplitLine
                  drawerLayout
                />
              </Box>
            ) : null}

            {hasSplitsAssigned ? null : (
              <Box className={classes.halfWidthField}>
                <ExclusionsButton
                  spendingPlan={Boolean(visibilityF2SDisplay.value)}
                  reports={Boolean(visibilityExcludeReportsDisplay.value)}
                  onToggle={onToggleExclusion}
                  disabledReports={disableReports}
                  transfer={
                    selectedType === 'TRANSFER'
                      ? {
                          excludeF2S: Boolean(transaction.isExcludedFromF2S),
                          excludeF2SDest: Boolean(transaction.excludeF2SDest),
                          excludeReports: Boolean(transaction.isExcludedFromReports),
                          excludeReportsDest: Boolean(transaction.excludeReportsDest),
                          onChangeF2S: (value) => {
                            form.setters.setFieldValue('transaction.excludeF2SUserOverridden', true);
                            form.setters.setFieldValue('transaction.isExcludedFromF2S', value);
                          },
                          onToggleF2SDest: () =>
                            form.setters.setFieldValue('transaction.excludeF2SDest', !transaction.excludeF2SDest),
                          onChangeReports: (value) =>
                            form.setters.setFieldValue('transaction.isExcludedFromReports', value),
                          onToggleReportsDest: () =>
                            form.setters.setFieldValue(
                              'transaction.excludeReportsDest',
                              !transaction.excludeReportsDest,
                            ),
                        }
                      : undefined
                  }
                />
              </Box>
            )}
          </Box>

          {hasSplitsAssigned ? <SplitExclusionNotice onEditSplits={() => onAssignSplits?.()} /> : null}
        </Box>
      </Box>

      <Divider />

      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant='bodyMedium'>Amount</Typography>
          <Typography variant='paragraphRegular'>This is an estimate until your actual payment arrives.</Typography>
        </Box>
        <AmountCard
          isIncome={isIncome}
          amountError={_get(errors, 'transaction.amount')}
          form={form}
          create={Boolean(create)}
          amountMatchVarianceFF={amountMatchVarianceFF}
          amountSlot={
            hasSplitsAssigned ? (
              <QPanelButton
                id='edit-split-amount'
                onClick={() => {
                  const amountForTracking = typeof transaction.amount === 'number' ? transaction.amount : 0;
                  tracker.track(tracker.events.viewSplitsV2, {
                    initiator: transaction.id === null ? 'createTxn' : 'editTxnWithSplits',
                    transactionAmount: amountForTracking,
                    transactionAmountSign: amountForTracking >= 0 ? 'positive' : 'negative',
                  });
                  onAssignSplits?.();
                }}
                style={{ margin: 0, padding: 0, height: '100%' }}
                title={
                  <QAmountField
                    value={transaction.amount}
                    expectedSign={isIncome ? 'positive' : 'negative'}
                    id='schedtxn-amount'
                    label='Amount'
                    fullWidth
                    autoComplete='one-time-code'
                    showSign='positive'
                    showColor='positive'
                  />
                }
                arrow={
                  <IconButton size='small'>
                    <ChevronRightIcon fontSize='small' />
                  </IconButton>
                }
              />
            ) : undefined
          }
        />
      </Box>

      <Divider />

      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant='bodyMedium'>Frequency & Occurrence</Typography>
          <Typography variant='paragraphRegular'>Sets the frequency of your Reminders.</Typography>
        </Box>
        {renderDateAndFrequencySummary(form)}
      </Box>

      {showBillConnect ? (
        <>
          <Divider />
          <Box>
            <BillConnectButton onClick={() => setTabValue(2)} form={form} />
          </Box>
        </>
      ) : null}

      {!create && scheduledTransaction?.isUserVerified && !scheduledTransaction?.isCompleted && onEndSeries ? (
        <>
          <Divider />
          <Box className={classes.fieldRow} alignItems='center'>
            <Button variant='outlined' onClick={onEndSeries}>
              End Series
            </Button>
            <Typography variant='paragraphRegular' color='textSecondary'>
              Stops this Series from generating Reminders. It will be kept for your records.
            </Typography>
          </Box>
        </>
      ) : null}
    </Box>
  );
}

BasicTab.displayName = 'stBasicTab';

export default BasicTab;
